import { IconButton, TextField, Typography } from '@mui/material';
import { PaperPlaneRight, X } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { ChatStreamEvent } from '../../data/responses/chatbot';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { axiosInstance } from '../../service/ApiService';
import { LocalStorageService } from '../../service/LocalStorageService';
import { generateRandomId } from '../../util/generateId';
import {
  MiniChatContainer,
  MiniChatHeader,
  MiniChatInputArea,
  MiniChatMessages,
  MiniMessageBubble,
  MiniMessageContainer,
  MiniTypingIndicator,
  OutputPreviewCard,
  InsertButton,
} from './StyledComponents';
import {
  PROMPT_ASSISTANT_FLOW_ID,
  PROMPT_ASSISTANT_INPUT_ID,
  ConnectedNodes,
  PromptAssistantMessage,
} from './types';

interface PromptAssistantChatProps {
  fieldName: string;
  nodeData: Record<string, unknown>;
  textValue: string;
  flowSummary: string;
  connectedNodes?: ConnectedNodes;
  onOutput: (output: string) => void;
  onVariables?: (variables: string[]) => void;
  onClose: () => void;
  sessionId?: string;
}

interface PendingOutput {
  text: string;
  variables: string[];
  rawResponse: string;
}

const extractOutput = (text: string): string | null => {
  const match = text.match(/<output>([\s\S]*?)<\/output>/);
  return match ? match[1].trim() : null;
};

const extractVariables = (text: string): string[] => {
  const regex = /<variable>([\s\S]*?)<\/variable>/g;
  const variables: string[] = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    const name = match[1].trim();
    if (name && variables.length < 5) {
      variables.push(name);
    }
  }
  return variables;
};

const stripTags = (text: string): string => {
  return text
    .replace(/<output>[\s\S]*?<\/output>/g, '')
    .replace(/<variable>[\s\S]*?<\/variable>/g, '')
    .trim();
};

const CONTEXT_CHAR_LIMIT = 120000;
const TRUNCATION_MARKER = '...[truncated]';

const findLargestStringPath = (obj: unknown, path: string[] = []): { path: string[]; length: number } | null => {
  if (typeof obj === 'string') return { path, length: obj.length };
  if (typeof obj === 'object' && obj !== null) {
    const entries: [string, unknown][] = Array.isArray(obj)
      ? obj.map((v, i) => [String(i), v])
      : Object.entries(obj as Record<string, unknown>);
    let largest: { path: string[]; length: number } | null = null;
    for (const [key, value] of entries) {
      const result = findLargestStringPath(value, [...path, key]);
      if (result && (!largest || result.length > largest.length)) largest = result;
    }
    return largest;
  }
  return null;
};

const getAtPath = (obj: Record<string, unknown>, path: string[]): string =>
  path.reduce((cur, key) => (cur as Record<string, unknown>)[key], obj as unknown) as string;

const setAtPath = (obj: Record<string, unknown>, path: string[], value: string): void => {
  const last = path[path.length - 1];
  const parent = path.slice(0, -1).reduce((cur, key) => (cur as Record<string, unknown>)[key], obj as unknown) as Record<string, unknown>;
  parent[last] = value;
};

const buildMessageText = (contextData: Record<string, unknown>, userMessage: string): string =>
  `[Context]: ${JSON.stringify(contextData)}\n\n[User Message]: ${userMessage}`;

const truncateContextToLimit = (contextData: Record<string, unknown>, userMessage: string): string => {
  const mutable: Record<string, unknown> = JSON.parse(JSON.stringify(contextData));
  let text = buildMessageText(mutable, userMessage);
  while (text.length > CONTEXT_CHAR_LIMIT) {
    const largest = findLargestStringPath(mutable);
    if (!largest || largest.length <= TRUNCATION_MARKER.length) break;
    const excess = text.length - CONTEXT_CHAR_LIMIT;
    const current = getAtPath(mutable, largest.path);
    const newLength = Math.max(0, current.length - excess - TRUNCATION_MARKER.length);
    setAtPath(mutable, largest.path, current.slice(0, newLength) + TRUNCATION_MARKER);
    text = buildMessageText(mutable, userMessage);
  }
  return text;
};

const PromptAssistantChat = ({
  fieldName,
  nodeData,
  textValue,
  flowSummary,
  connectedNodes,
  onOutput,
  onVariables,
  onClose,
  sessionId,
}: PromptAssistantChatProps) => {
  const { user } = useAuthStore(state => state);
  const { flowId } = useFlowsStore(state => state);
  const isDark = user?.theme === 'dark';

  const [messages, setMessages] = useState<PromptAssistantMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [socketClient, setSocketClient] = useState<Socket | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [pendingOutput, setPendingOutput] = useState<PendingOutput | null>(null);

  const sessionIdRef = useRef<string>(sessionId ?? generateRandomId());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamingContentRef = useRef('');
  const hassentFirstMessageRef = useRef(false);
  const onOutputRef = useRef(onOutput);
  const onVariablesRef = useRef(onVariables);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onOutputRef.current = onOutput;
    onVariablesRef.current = onVariables;
    onCloseRef.current = onClose;
  }, [onOutput, onVariables, onClose]);

  const handleOutputDetected = (fullText: string) => {
    const output = extractOutput(fullText);
    if (output !== null) {
      const vars = extractVariables(fullText);
      const cleanMessage = stripTags(fullText);

      setMessages(prev => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
          if (!cleanMessage) return updated.slice(0, lastIdx);
          updated[lastIdx] = { ...updated[lastIdx], content: cleanMessage };
        }
        return updated;
      });

      setPendingOutput({ text: output, variables: vars, rawResponse: fullText });
    }
  };

  const handleInsert = () => {
    if (!pendingOutput) return;
    if (pendingOutput.variables.length > 0 && onVariablesRef.current) {
      onVariablesRef.current(pendingOutput.variables);
    }
    onOutputRef.current(pendingOutput.text);
    onCloseRef.current();
  };

  useEffect(() => {
    const productionUrl = import.meta.env.VITE_API_URL_PRODUCTION as string;
    const socket = io(productionUrl, {
      auth: { token: LocalStorageService.getItem('accessToken') },
    });

    socket.on('connect', () => {
      setSocketClient(socket);
    });

    socket.on('chatbot-req', (socketResponse: ChatStreamEvent) => {
      if (socketResponse.data.type === 'stream') {
        const socketData = socketResponse.data;

        switch (socketData.event) {
          case 'stream_start':
            streamingContentRef.current = '';
            setPendingOutput(null);
            setMessages(prev => [
              ...prev,
              {
                role: 'assistant',
                content: '',
                timestamp: socketData.data.timestamp,
              },
            ]);
            break;

          case 'stream_message':
            streamingContentRef.current += socketData.data.text;
            setMessages(prev => {
              const updated = [...prev];
              const lastIdx = updated.length - 1;
              if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                updated[lastIdx] = {
                  ...updated[lastIdx],
                  content: updated[lastIdx].content + socketData.data.text,
                };
              }
              return updated;
            });
            break;

          case 'stream_end': {
            setIsTyping(false);
            handleOutputDetected(streamingContentRef.current);
            break;
          }

          case 'stream_error':
            setIsTyping(false);
            break;

          default:
            break;
        }
      } else {
        const socketData = socketResponse.data;

        switch (socketData.event) {
          case 'message': {
            const text = socketData.data?.[0]?.text ?? '';
            const timestamp =
              socketData.data?.[0]?.timestamp ?? new Date().toISOString();

            setMessages(prev => [
              ...prev,
              { role: 'assistant', content: text, timestamp },
            ]);
            setIsTyping(false);

            handleOutputDetected(text);
            break;
          }

          case 'error':
            setIsTyping(false);
            break;

          default:
            break;
        }
      }
    });

    return () => {
      socket.off('connect');
      socket.off('chatbot-req');
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, pendingOutput]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !socketClient?.id) return;

    if (!expanded) {
      setExpanded(true);
    }

    setPendingOutput(null);

    const userMessage: PromptAssistantMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    setInputValue('');

    const isFirstMessage = !hassentFirstMessageRef.current;
    hassentFirstMessageRef.current = true;

    let messageText = userMessage.content;

    if (isFirstMessage) {
      const contextData: Record<string, unknown> = {
        flowSummary,
        flowId: flowId || '',
        sessionId: sessionIdRef.current,
        nodeJson: nodeData,
        fieldBeingEdited: fieldName,
        existingFieldValue: textValue,
        connectedNodes,
      };
      messageText = truncateContextToLimit(contextData, userMessage.content);
    }

    const variableInputs: Array<{ inputText?: string; inputId?: string }> = [
      {
        inputId: PROMPT_ASSISTANT_INPUT_ID,
        inputText: messageText,
      },
    ];

    try {
      const productionUrl = import.meta.env.VITE_API_URL_PRODUCTION as string;
      const sessionId = sessionIdRef.current;
      await axiosInstance.post(
        `${productionUrl}/flux/chatbot-api-socket?flowId=${PROMPT_ASSISTANT_FLOW_ID}&sessionId=${sessionId}&socketId=${socketClient.id}`,
        { sessionId, variableInputs },
      );
    } catch {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <MiniChatContainer isDark={isDark} expanded={expanded}>
      <MiniChatHeader isDark={isDark}>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            color: isDark ? '#F2F4F7' : '#101828',
          }}
        >
          Prompt Assistant
        </Typography>
        <IconButton onClick={onClose} sx={{ p: '4px' }}>
          <X size={16} color={isDark ? '#98A2B3' : '#475467'} />
        </IconButton>
      </MiniChatHeader>

      {expanded ? (
        <MiniChatMessages>
          {messages.map((msg, index) => (
            <MiniMessageContainer key={index} isUser={msg.role === 'user'}>
              <MiniMessageBubble isUser={msg.role === 'user'} isDark={isDark}>
                {msg.content}
              </MiniMessageBubble>
            </MiniMessageContainer>
          ))}

          {isTyping && messages[messages.length - 1]?.role !== 'assistant' && (
            <MiniTypingIndicator isDark={isDark}>
              <span className="dot" />
              <span className="dot" />
              <span className="dot" />
            </MiniTypingIndicator>
          )}

          <div ref={messagesEndRef} />
        </MiniChatMessages>
      ) : (
        <Typography
          sx={{
            padding: '16px 14px 8px',
            fontSize: '13px',
            color: isDark ? '#98A2B3' : '#667085',
          }}
        >
          What can I help you with?
        </Typography>
      )}

      {pendingOutput && (
        <>
          <OutputPreviewCard isDark={isDark}>
            {pendingOutput.text}
          </OutputPreviewCard>
          <InsertButton variant="contained" onClick={handleInsert} fullWidth={false}>
            Insert
          </InsertButton>
        </>
      )}

      <MiniChatInputArea isDark={isDark}>
        <TextField
          fullWidth
          size="small"
          placeholder="Describe what you need..."
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!socketClient?.connected}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '13px',
              borderRadius: '8px',
              backgroundColor: isDark ? '#101828' : '#F9FAFB',
              color: isDark ? '#D0D5DD' : '#101828',
              '& fieldset': {
                borderColor: isDark ? '#344054' : '#D0D5DD',
              },
              '&:hover fieldset': {
                borderColor: isDark ? '#475467' : '#98A2B3',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3650DE',
              },
            },
          }}
        />
        <IconButton
          onClick={handleSendMessage}
          disabled={!inputValue.trim() || !socketClient?.connected}
          sx={{
            p: '6px',
            backgroundColor: '#3650DE',
            borderRadius: '8px',
            '&:hover': { backgroundColor: '#2A40B0' },
            '&.Mui-disabled': {
              backgroundColor: isDark ? '#344054' : '#D0D5DD',
            },
          }}
        >
          <PaperPlaneRight size={16} color="#FFFFFF" weight="fill" />
        </IconButton>
      </MiniChatInputArea>
    </MiniChatContainer>
  );
};

export default PromptAssistantChat;
